// Placeholder for template.api.ts

import APIService from '../../../services/APIService';
import {
  AnyTemplate,
  CreateTemplatePayload,
  PaginatedTemplatesResponse,
  TemplateListParams,
  UpdateTemplatePayload,
} from './template.types';

const BASE_URL = '/admin/templates'; // Adjusted to match backend routes

/**
 * Fetches templates with optional pagination, filtering, and sorting
 */
export const fetchTemplates = async (
  params: TemplateListParams = {},
  accessToken: string,
): Promise<PaginatedTemplatesResponse> => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, String(value));
    }
  });

  const response = await APIService.makeAPIGetRequest({
    requestString: `${BASE_URL}?${queryParams.toString()}`,
    accessToken,
  });

  if (response.ok) {
    return response.value as PaginatedTemplatesResponse;
  }

  throw new Error('Failed to fetch templates');
};

/**
 * Fetches a single template by ID and version
 */
export const fetchTemplate = async (
  templateId: string,
  templateVersion: number,
  templateType: 'public' | 'user',
  accessToken: string,
): Promise<{ data: AnyTemplate }> => {
  // Note: Backend returns { data: AnyTemplate }, so matching that here
  const response = await APIService.makeAPIGetRequest({
    requestString: `${BASE_URL}/${templateId}/${templateVersion}?type=${templateType}`,
    accessToken,
  });

  if (response.ok) {
    return response.value as { data: AnyTemplate };
  }

  throw new Error(`Failed to fetch template ${templateId}/${templateVersion}`);
};

/**
 * Creates a new template
 */
export const createTemplate = async (
  templateData: CreateTemplatePayload,
  accessToken: string,
): Promise<{ status: number; data: AnyTemplate }> => {
  // Note: Backend returns { status: number, data: AnyTemplate }
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/create`,
    accessToken,
    body: templateData,
  });

  if (response.ok) {
    return response.value as { status: number; data: AnyTemplate };
  }

  throw new Error('Failed to create template');
};

/**
 * Updates an existing template
 */
export const updateTemplate = async (
  templateId: string,
  templateVersion: number,
  templateType: 'public' | 'user',
  templateData: UpdateTemplatePayload,
  accessToken: string,
): Promise<{ status: number; data: AnyTemplate }> => {
  // Note: Backend returns { status: number, data: AnyTemplate }
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/update?template_id=${templateId}&template_version=${templateVersion}&type=${templateType}`,
    accessToken,
    body: templateData,
  });

  if (response.ok) {
    return response.value as { status: number; data: AnyTemplate };
  }

  throw new Error(`Failed to update template ${templateId}/${templateVersion}`);
};

/**
 * Deletes a public template
 */
export const deletePublicTemplate = async (
  templateId: string,
  templateVersion: number,
  accessToken: string,
): Promise<{ status: number; message: string }> => {
  // Note: Backend returns { status: number, message: string }
  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/delete?template_id=${templateId}&template_version=${templateVersion}&type=public`,
    accessToken,
  });

  if (response.ok) {
    return response.value as { status: number; message: string };
  }
  throw new Error(`Failed to delete public template ${templateId}/${templateVersion}`);
};

/**
 * Copies an existing template
 */
export const copyTemplate = async (
  params: {
    source_template_id: string;
    source_template_version: number;
    source_type: 'public' | 'user';
    target_type: 'public' | 'user';
    new_name?: string;
  },
  accessToken: string,
): Promise<{ status: number; data: AnyTemplate }> => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.append(key, String(value));
    }
  });

  const response = await APIService.makeAPIPostRequest({
    requestString: `${BASE_URL}/copy?${queryParams.toString()}`,
    accessToken,
    // No body for copy, params are in query string as per backend
  });

  if (response.ok) {
    return response.value as { status: number; data: AnyTemplate };
  }

  throw new Error('Failed to copy template');
};

// Note: User templates are not directly deleted. They might be deactivated or managed differently.
// If there's a deactivate/activate mechanism for user templates (similar to user modules),
// those functions would be added here.
// For now, only public template deletion is included based on the backend routes.
